import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Footprints, RefreshCw, HelpCircle, BatteryCharging } from "lucide-react";
import { Button } from "./ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

/**
 * StepsCard — shows today's step count, "last synced X min ago" timestamp,
 * a manual Refresh button that re-queries Health Connect, and an info
 * tooltip explaining where the data comes from. Only rendered on Android
 * (Health Connect platform) — Dashboard.jsx gates this behind
 * `isHealthSupported()`.
 *
 * Tester request v42: "In the steps card, add a 'Last synced' timestamp
 * showing when Health Connect data was last read, and add a manual
 * 'Refresh' button that re-queries Health Connect. Also add a short
 * tooltip explaining that steps come from Health Connect."
 */
export default function StepsCard({ steps, lastSyncAt, syncing, onRefresh, onHelp, refillsEarnedToday = 0, refillsCap = 10 }) {
  const { t } = useTranslation();
  const hasSteps = steps > 0;
  const relativeWhen = useRelativeTime(lastSyncAt);
  const [showRefillHelp, setShowRefillHelp] = useState(false);

  return (
    <div
      data-testid="card-steps"
      className="p-4 rounded-2xl bg-white border border-[#E6E2D8] flex flex-col gap-2.5"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-[#5F7161]/10 flex items-center justify-center shrink-0">
            <Footprints size={18} strokeWidth={1.6} className="text-[#5F7161]" />
          </div>
          <div className="min-w-0">
            <p className="text-[9px] uppercase tracking-[0.2em] text-[#8AA48F]">
              {t("dashboard.steps_card_title")}
            </p>
            <p
              data-testid="steps-card-value"
              style={{ fontFamily: "Outfit, sans-serif" }}
              className="text-2xl font-semibold tabular-nums leading-tight"
            >
              {hasSteps ? steps.toLocaleString() : t("dashboard.steps_card_value_unknown")}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  data-testid="steps-card-info"
                  aria-label="info"
                  className="p-2 rounded-full hover:bg-[#F7F5F0] text-[#5F7161]/70 hover:text-[#5F7161] active:scale-95 transition"
                  onClick={onHelp}
                >
                  <HelpCircle size={16} strokeWidth={1.5} />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="left"
                className="max-w-[240px] text-xs leading-relaxed bg-[#2C332A] text-[#F7F5F0] border-[#2C332A]"
              >
                {t("dashboard.steps_card_tooltip")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Button
            data-testid="steps-card-refresh"
            variant="outline"
            size="sm"
            disabled={syncing}
            onClick={onRefresh}
            className="rounded-full border-[#E6E2D8] hover:bg-[#F7F5F0] h-8 px-3"
          >
            <RefreshCw size={13} strokeWidth={1.5} className={`${syncing ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <p data-testid="steps-card-last-synced" className="text-[11px] text-[#2C332A]/55">
          {lastSyncAt
            ? t("dashboard.steps_card_last_synced", { when: relativeWhen })
            : t("dashboard.steps_card_never_synced")}
        </p>
        <div data-testid="steps-card-refills-row" className="flex items-center gap-1.5 shrink-0">
          <BatteryCharging size={14} strokeWidth={1.8} className="text-[#D49B3B]" />
          <span
            data-testid="steps-card-refills-value"
            className="text-[11px] font-bold text-[#8B6F3A] tabular-nums"
          >
            {t("dashboard.steps_card_refills_count", { got: refillsEarnedToday, cap: refillsCap })}
          </span>
          <button
            type="button"
            data-testid="steps-card-refills-help-toggle"
            onClick={() => setShowRefillHelp((v) => !v)}
            className="text-[#8B6F3A]/70 hover:text-[#6B4A1E]"
            aria-label="How refills work"
          >
            <HelpCircle size={12} strokeWidth={2} />
          </button>
        </div>
      </div>
      <div
        data-testid="steps-card-refills-progress"
        className="w-full h-1 rounded-full bg-[#E8D8B4]/60 overflow-hidden"
      >
        <div
          className="h-full bg-gradient-to-r from-[#E8B86B] to-[#D49B3B] rounded-full transition-all duration-500"
          style={{ width: `${Math.min(100, Math.round((refillsEarnedToday / Math.max(1, refillsCap)) * 100))}%` }}
        />
      </div>
      {showRefillHelp && (
        <p data-testid="steps-card-refills-help" className="text-[11px] leading-snug text-[#6B4A1E]/70">
          {t("dashboard.steps_card_refills_help", { cap: refillsCap })}
        </p>
      )}
    </div>
  );
}

/**
 * Resolves an ISO timestamp into a localized "X min ago" string that
 * auto-updates every 30s while the dashboard is open. Returns "" if the
 * input is missing or unparseable. Implemented as state-driven so we
 * don't read `Date.now()` during render (React 19 react-hooks/purity
 * rule disallows side-effects in the render phase).
 */
function useRelativeTime(iso) {
  const { t } = useTranslation();
  const [label, setLabel] = useState("");
  React.useEffect(() => {
    // No timestamp → render an empty label and bail. State writes inside
    // an effect are the documented React 19 pattern for deriving
    // formatted text from an external clock (Date.now()) — flagged by
    // react-hooks/set-state-in-effect, but we can't avoid the side
    // effect here without re-introducing impure render-time work.
    const recompute = () => {
      if (!iso) {
        setLabel("");
        return;
      }
      const past = new Date(iso).getTime();
      if (Number.isNaN(past)) {
        setLabel("");
        return;
      }
      const diffSec = Math.max(0, Math.round((Date.now() - past) / 1000));
      if (diffSec < 30) return setLabel(t("dashboard.steps_card_just_now"));
      if (diffSec < 60) return setLabel(t("dashboard.steps_card_seconds_ago", { n: diffSec }));
      const min = Math.round(diffSec / 60);
      if (min < 60) return setLabel(t("dashboard.steps_card_minutes_ago", { n: min }));
      const hr = Math.round(min / 60);
      if (hr < 24) return setLabel(t("dashboard.steps_card_hours_ago", { n: hr }));
      const day = Math.round(hr / 24);
      return setLabel(t("dashboard.steps_card_days_ago", { n: day }));
    };
    recompute();
    const id = setInterval(recompute, 30 * 1000);
    return () => clearInterval(id);
  }, [iso, t]);
  return label;
}
